import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApiError } from "~/hooks";
import documentService from "~/services/documents";
import type { RawOrgDocument } from "~/services/organizations";
import { QueryKey } from "~/utils";

export const getRawOrgDocumentQueryKey = (orgDocumentId = "") => [QueryKey.Org, "document", orgDocumentId];

export const fetchOrgDocument = async (orgDocumentId = "") =>
  documentService.getDocument<RawOrgDocument>(orgDocumentId);

type UseRawOrgDocumentQueryOptions<T> = {
  documentId?: string;
} & Omit<UseQueryOptions<RawOrgDocument, Error, T>, "queryKey" | "queryFn">;

export const useRawOrgDocumentQuery = <T = RawOrgDocument>({
  documentId,
  enabled = true,
  ...queryOptions
}: UseRawOrgDocumentQueryOptions<T> = {}) => {
  const { onApiError } = useApiError();

  const query = useQuery({
    queryKey: getRawOrgDocumentQueryKey(documentId),
    queryFn: () => fetchOrgDocument(documentId),
    enabled: !!documentId && enabled,
    ...queryOptions,
  });

  useEffect(() => {
    if (query.error) {
      onApiError(query.error);
    }
  }, [onApiError, query.error]);

  return query;
};

export const getAxleDataFromOrgDocument = (orgDocument?: RawOrgDocument) => orgDocument?.data.axle.data["axle.org"];

export const getHUBDataFromOrgDocument = (orgDocument?: RawOrgDocument) => orgDocument?.data.hub?.data["hub.org"];
